/**
 * Koordinaten nachtragen.
 *
 * Eine eigene, schlanke Leaflet-Instanz: Die Weltkarte in `views/karte.js`
 * gruppiert Marker und sucht auf Klick den Umkreis — hier setzt ein Klick eine
 * Nadel. Gegensätzliches Verhalten gehört nicht in eine Datei.
 *
 * **Der Klick speichert nicht.** Er setzt eine Nadel, die man sieht, verschieben
 * und wieder entfernen kann; gespeichert wird eigens über den Knopf. Vorher war
 * es umgekehrt, und ein danebengegangener Klick stand sofort in den Stammdaten.
 *
 * **Die Liste zeigt alles.** 386 Plätze sind zu viele, um sie der Reihe nach
 * abzuarbeiten: Wer gerade weiss, wo „Lopper III" liegt, will dorthin springen
 * und nicht vierzehn Mal „Überspringen" drücken. Deshalb Volltextsuche über
 * Name, Land, Region, Gewässer, Notiz und Reise — und auf Wunsch auch die
 * bereits verorteten, sonst liesse sich eine falsch gesetzte Position nie
 * wiederfinden.
 *
 * **Nach Reise statt nach Platz.** 182 der offenen Plätze hängen an zwölf
 * Reisen, die schon verortete Plätze tragen. Der zweite Arbeitsmodus zeigt
 * eine Reise als Tagesfolge — die Route, wie sie getaucht wurde — und lässt
 * die Lücken in einer Sitzung füllen, statt zwölfmal denselben Ausschnitt
 * wiederzusuchen. Die Rechnung dazu steht in `reisen.js`.
 *
 * **Vorschläge statt leerer Karte.** Zu jedem Platz rechnet `verortung.js` aus,
 * welche verorteten Plätze in Frage kommen — gleicher Name, Zusatzwort,
 * Schreibvariante, Nummerngeschwister, dieselbe Reise. Sie liegen als Marker
 * auf der Karte und als Liste darunter; ein Klick setzt die Nadel dorthin,
 * gespeichert wird immer noch eigens.
 */

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { SPEICHER, alleAus, alleTauchgaenge } from '../db.js';
import { koordinaten } from '../format.js';
import { grundwahl, grundwahlLeiste, haltGroesseNach } from '../kartengrund.js';
import { speichere } from '../saetze.js';
import { geraeteKennung } from '../sync.js';
import { nachbarn, setzePosition, waehleAnker } from '../verorten.js';
import { reiseListe, reiseplan } from '../reisen.js';
import {
  arbeitsliste, darfVerschmelzen, filtere, herkunftstext, leseKoordinaten,
  tauchgangszahl, vorschlaege, weiterePlaetze,
} from '../verortung.js';
import { fuehreZusammen } from '../zusammenfuehren.js';
import { frageZusammenfuehren } from './zusammenfuehrdialog.js';

const GRUND_TEXT = {
  reise: 'Ausschnitt nach einem Platz derselben Reise',
  land: 'Ausschnitt nach einem Platz im selben Land',
  landesmitte: 'Ausschnitt nach dem Land — noch kein Platz dort verortet',
  welt: 'Kein Land hinterlegt — Weltkarte',
};

const ZOOM_NADEL = 14;

// Ein Fadenkreuz, kein Tropfen: Gemeint ist genau dieser Punkt, nicht "hier
// unten irgendwo". Als divIcon gebaut, damit Leaflet keine Bilddatei braucht —
// die findet ein Bündler ohne Zutun nicht.
const NADEL = L.divIcon({ className: 'nadel-symbol', html: '<span></span>', iconSize: [24, 24], iconAnchor: [12, 12] });

function marke(platz) {
  const text = herkunftstext(platz);
  return text ? `<span class="herkunftsmarke">${text}</span>` : '';
}

// -------------------------------------------------------------------- Liste

function zeichneListe(wurzel) {
  const { zustand } = wurzel;
  const sichtbar = filtere(
    zustand.auchVerortete ? zustand.eintraege : zustand.eintraege.filter((e) => !e.verortet),
    zustand.suchtext,
  );

  const liste = wurzel.querySelector('.schlange');
  liste.replaceChildren(...sichtbar.slice(0, zustand.grenze).map((eintrag) => {
    const li = document.createElement('li');
    li.className = 'schlangezeile';
    if (eintrag.platz.id === zustand.gewaehlt) li.classList.add('aktuell');
    if (eintrag.verortet) li.classList.add('verortet');
    li.innerHTML = `<button type="button" data-id="${eintrag.platz.id}">
        <span class="platzname">${eintrag.platz.name ?? 'ohne Namen'}</span>
        <span class="platzzahl">${eintrag.anzahl} TG</span>
        <span class="platzort">${[eintrag.platz.country, ...eintrag.reisen.slice(0, 1)]
    .filter(Boolean).join(' · ')}</span>
        ${eintrag.verortet ? marke(eintrag.platz) || '<span class="herkunftsmarke">verortet</span>' : ''}
      </button>`;
    return li;
  }));

  const rest = sichtbar.length - zustand.grenze;
  const mehr = wurzel.querySelector('.mehr-laden');
  // Im Reisemodus gehört der Knopf nicht zur sichtbaren Liste. Ohne diese
  // Bedingung holte ihn jedes `zeichneListe()` zurück, das aus `waehle()`
  // kommt — sichtbar wurde das erst im gebauten Stand.
  mehr.hidden = rest <= 0 || zustand.modus === 'reise';
  mehr.textContent = rest > 0 ? `${rest} weitere zeigen` : '';

  wurzel.querySelector('.listenstand').textContent = sichtbar.length === zustand.eintraege.length
    ? `${sichtbar.length} Plätze`
    : `${sichtbar.length} von ${zustand.eintraege.length} Plätzen`;
}

// -------------------------------------------------------------------- Reisen

// `timeZone: 'UTC'` ist Absicht: Der Tag ist bereits die Ortszeit des
// Tauchplatzes. Ohne das rechnete der Browser ihn in seine eigene Zone um und
// verschöbe die Überschrift um einen Tag.
const TAG_FORM = new Intl.DateTimeFormat('de-CH', {
  day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
});

function tagestext(tag) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(tag)) return tag;
  return TAG_FORM.format(new Date(`${tag}T12:00:00Z`));
}

function zeichneReisewahl(wurzel) {
  const { zustand } = wurzel;
  const wahl = wurzel.querySelector('.reisewahl');
  // Wird die letzte Nadel einer Reise gesetzt, fällt sie aus `reiseListe()`
  // heraus — mitten in der Arbeit daran. Sie bleibt deshalb in der Auswahl
  // stehen, solange sie aufgeschlagen ist, und meldet "0 offen".
  const gezeigt = [...zustand.reisen];
  if (zustand.reise && !gezeigt.some((r) => r.id === zustand.reise)) {
    const fertig = zustand.alleReisen?.find((r) => r.id === zustand.reise);
    if (fertig) gezeigt.push({ ...fertig, offen: 0 });
  }
  wahl.replaceChildren(...gezeigt.map((reise) => {
    const option = document.createElement('option');
    option.value = reise.id;
    // Eine Reise ohne verorteten Platz ist kein Sonderfall, sondern der
    // Anfang: In ihrem Land ist noch nichts verortet, und die erste Nadel holt
    // alle übrigen eine Stufe höher. Das gehört an die Auswahl, sonst sieht sie
    // aus wie die unattraktivste Zeile der Liste.
    option.textContent = `${reise.name} — ${reise.offen} offen, `
      + (reise.verortet === 0 ? 'kein verorteter Platz' : `${reise.verortet} verortet`);
    return option;
  }));
  if (gezeigt.length === 0) {
    const option = document.createElement('option');
    option.textContent = 'Keine Reise mit offenen Plätzen';
    wahl.append(option);
    wahl.disabled = true;
    return;
  }
  wahl.disabled = false;
  wahl.value = gezeigt.some((r) => r.id === zustand.reise) ? zustand.reise : gezeigt[0].id;
}

/**
 * Eine Reise als Tagesfolge.
 *
 * Verortete Plätze stehen mit dazwischen, blass und nicht anklickbar zum
 * Weiterarbeiten, aber sichtbar: Der verortete Platz zwischen zwei offenen ist
 * die eigentliche Auskunft darüber, wo die beiden liegen. Sie lassen sich
 * trotzdem wählen — eine falsch gesetzte Position will man auch hier
 * korrigieren können.
 */
function zeichneReiseplan(wurzel) {
  const { zustand } = wurzel;
  const bereich = wurzel.querySelector('.reiseplan');
  if (!zustand.reise) { bereich.replaceChildren(); return; }

  const tage = reiseplan(zustand.reise, zustand.plaetze, zustand.tauchgaenge);
  const reise = zustand.reisen.find((r) => r.id === zustand.reise);
  const kopf = document.createElement('p');
  kopf.className = 'reisestand';
  kopf.textContent = reise
    ? `${reise.offen} ohne Koordinaten · ${reise.verortet} verortet · ${tage.length} Tauchtage`
    : '';
  kopf.classList.toggle('ohne-anker', Boolean(reise) && reise.verortet === 0);
  if (reise && reise.verortet === 0 && reise.offen > 1) {
    kopf.textContent += ` — die erste Nadel gibt den übrigen ${reise.offen - 1} `
      + 'einen Anhaltspunkt';
  }

  bereich.replaceChildren(kopf, ...tage.map(({ tag, eintraege }) => {
    const block = document.createElement('div');
    block.className = 'reisetag';
    block.innerHTML = `<h3>${tagestext(tag)}</h3><ol>${eintraege.map((zeile) => `
      <li class="${zeile.verortet ? 'verortet' : 'offen'}${
  zeile.platz.id === zustand.gewaehlt ? ' aktuell' : ''}">
        <button type="button" data-id="${zeile.platz.id}">
          <span class="platzname">${zeile.platz.name ?? 'ohne Namen'}</span>
          <span class="platzzahl">${zeile.nummern.length
    ? `Nr. ${zeile.nummern.join(', ')}` : 'ohne Nummer'}</span>
          ${zeile.verortet ? marke(zeile.platz) || '<span class="herkunftsmarke">verortet</span>' : ''}
        </button>
      </li>`).join('')}</ol>`;
    return block;
  }));
}

/** Umschalten zwischen der ganzen Liste und einer Reise. */
function zeichneModus(wurzel) {
  const { zustand } = wurzel;
  const nachReise = zustand.modus === 'reise';
  for (const knopf of wurzel.querySelectorAll('.modus')) {
    knopf.classList.toggle('aktiv', knopf.dataset.modus === zustand.modus);
    knopf.setAttribute('aria-pressed', String(knopf.dataset.modus === zustand.modus));
  }
  wurzel.querySelector('.reisewahl').hidden = !nachReise;
  wurzel.querySelector('.reiseplan').hidden = !nachReise;
  wurzel.querySelector('.schlange').hidden = nachReise;
  wurzel.querySelector('.platzsuche').hidden = nachReise;
  wurzel.querySelector('.auch-verortete').hidden = nachReise;
  wurzel.querySelector('.listenstand').hidden = nachReise;
  if (nachReise) wurzel.querySelector('.mehr-laden').hidden = true;
  else zeichneListe(wurzel);
}

/**
 * Eine Reise aufschlagen: Plan links, Ausschnitt über ihre verorteten Plätze.
 *
 * Der Ausschnitt kommt **nach** der Platzwahl, nicht davor. `waehle()` rahmt
 * die Vorschläge des einzelnen Platzes ein — bei einer Reise mit einem einzigen
 * verorteten Nachbarn ist das ein Punkt in voller Vergrösserung, und von der
 * Gegend sieht man nichts. Beim Aufschlagen zählt die Reise; wer danach eine
 * Zeile anklickt, bekommt wieder den engen Ausschnitt seines Platzes.
 */
function waehleReise(wurzel, reiseId) {
  const { zustand } = wurzel;
  zustand.reise = reiseId;
  zeichneReiseplan(wurzel);

  const zeilen = reiseplan(reiseId, zustand.plaetze, zustand.tauchgaenge)
    .flatMap((tag) => tag.eintraege);
  const erster = zeilen.find((z) => !z.verortet);
  if (erster) waehle(wurzel, erster.platz.id);
  else meldung(wurzel, 'Diese Reise ist vollständig verortet.');

  const punkte = zeilen.filter((z) => z.verortet).map((z) => [z.platz.lat, z.platz.lon]);
  if (punkte.length === 0) return;
  zustand.karte?.fitBounds(L.latLngBounds(punkte), { padding: [60, 60], maxZoom: 11 });
  const anzahl = punkte.length;
  wurzel.querySelector('.ankergrund').textContent =
    `Ausschnitt über ${anzahl} verortete${anzahl === 1 ? 'n Platz' : ' Plätze'} dieser Reise`;
}

function zeichneKopf(wurzel) {
  const { zustand } = wurzel;
  const offen = zustand.eintraege.filter((e) => !e.verortet).length;
  const vonHand = zustand.eintraege.filter((e) => e.manuell).length;
  wurzel.querySelector('.fortschritt').textContent = offen === 0
    ? `Alle ${zustand.eintraege.length} Plätze haben Koordinaten.`
    : `${offen} ohne Koordinaten · ${zustand.eintraege.length - offen} verortet`
      + (vonHand ? ` · davon ${vonHand} von Hand` : '');
}

// --------------------------------------------------------------- Vorschläge

function zeichneVorschlaege(wurzel) {
  const { zustand } = wurzel;
  const bereich = wurzel.querySelector('.vorschlaege');
  zustand.ebeneVorschlaege.clearLayers();

  if (!zustand.gewaehlt) {
    bereich.replaceChildren();
    return;
  }
  const platz = zustand.plaetze.find((p) => p.id === zustand.gewaehlt);
  const liste = vorschlaege(platz, zustand.plaetze, zustand.tauchgaenge);
  zustand.vorschlaege = liste;

  for (const eintrag of liste) {
    L.circleMarker([eintrag.lat, eintrag.lon], {
      // Kühles Cyan, nicht das Bernstein der Nadel: Ein Vorschlag ist ein
      // Angebot, die Nadel ist die Entscheidung. Gleiche Farbe hiesse, beides
      // sei dasselbe.
      radius: 9, color: 'rgb(58, 158, 190)', weight: 2,
      fillColor: 'rgb(58, 158, 190)', fillOpacity: 0.2,
    })
      .bindTooltip(`${eintrag.name} — ${eintrag.begruendung}`, { direction: 'top' })
      .on('click', () => setzeNadel(wurzel, eintrag.lat, eintrag.lon, `Vorschlag „${eintrag.name}"`))
      .addTo(zustand.ebeneVorschlaege);
  }

  bereich.innerHTML = liste.length === 0
    ? '<p class="keine-vorschlaege">Kein Vorschlag — dieser Platz hat keinen '
      + 'Namensverwandten und keine verortete Reise. Karte oder Koordinatenfeld.</p>'
    : `<h2>Vorschläge</h2><ul class="vorschlagsliste">${liste.map((eintrag, i) => `<li>
        <button type="button" data-vorschlag="${i}">
          <span class="vorschlagsname">${eintrag.name}</span>
          <span class="vorschlagsgrund">${eintrag.begruendung}</span>
          <span class="vorschlagsort">${koordinaten(eintrag.lat, eintrag.lon)}</span>
        </button>${darfVerschmelzen(eintrag)
    ? `<button type="button" class="knopf verschmelzen" data-verschmelzen="${i}"
            title="Beide Plätze sind derselbe — zusammenführen statt verorten"
          >Zusammenführen</button>` : ''}</li>`).join('')}</ul>`;
}

// -------------------------------------------------------------------- Nadel

function setzeNadel(wurzel, lat, lon, herkunft = 'Karte') {
  const { zustand } = wurzel;
  if (!zustand.gewaehlt) {
    meldung(wurzel, 'Erst einen Platz aus der Liste wählen.', true);
    return;
  }
  zustand.nadelPunkt = { lat, lon };
  if (zustand.nadel) {
    zustand.nadel.setLatLng([lat, lon]);
  } else {
    zustand.nadel = L.marker([lat, lon], { icon: NADEL, draggable: true, autoPan: true })
      .addTo(zustand.karte);
    // Verschieben ist die halbe Genauigkeit: Man klickt grob und rückt zurecht.
    zustand.nadel.on('dragend', () => {
      const ort = zustand.nadel.getLatLng();
      zustand.nadelPunkt = { lat: ort.lat, lon: ort.lng };
      zeichneNadelstand(wurzel, 'verschoben');
    });
  }
  if (!zustand.karte.getBounds().contains([lat, lon])) {
    zustand.karte.setView([lat, lon], Math.max(zustand.karte.getZoom(), ZOOM_NADEL));
  }
  zeichneNadelstand(wurzel, herkunft);
}

function entferneNadel(wurzel) {
  const { zustand } = wurzel;
  zustand.nadel?.remove();
  zustand.nadel = null;
  zustand.nadelPunkt = null;
  zeichneNadelstand(wurzel);
}

function zeichneNadelstand(wurzel, herkunft = null) {
  const { zustand } = wurzel;
  const stand = wurzel.querySelector('.nadelstand');
  const gesetzt = Boolean(zustand.nadelPunkt);
  wurzel.querySelector('.speichern').disabled = !gesetzt || !zustand.gewaehlt;
  wurzel.querySelector('.nadel-weg').disabled = !gesetzt;
  stand.textContent = gesetzt
    ? `Nadel: ${koordinaten(zustand.nadelPunkt.lat, zustand.nadelPunkt.lon)}`
      + (herkunft ? ` · ${herkunft}` : '') + ' — zum Feinjustieren ziehen'
    : 'Keine Nadel gesetzt. Auf die Karte klicken, einen Vorschlag wählen '
      + 'oder Koordinaten einfügen.';
}

function meldung(wurzel, text, fehler = false) {
  const feld = wurzel.querySelector('.meldung');
  feld.textContent = text;
  feld.classList.toggle('fehler', fehler);
}

// ------------------------------------------------------------------ Auswahl

function waehle(wurzel, id) {
  const { zustand } = wurzel;
  zustand.gewaehlt = id;
  entferneNadel(wurzel);
  meldung(wurzel, '');

  const platz = zustand.plaetze.find((p) => p.id === id);
  const kopf = wurzel.querySelector('.gewaehlter-platz');
  if (!platz) {
    kopf.textContent = 'Kein Platz gewählt.';
    zeichneListe(wurzel);
    zeichneReiseplan(wurzel);
    return;
  }

  const eintrag = zustand.eintraege.find((e) => e.platz.id === id);
  kopf.innerHTML = `<strong>${platz.name ?? 'ohne Namen'}</strong>`
    + ` <span class="leise">${tauchgangszahl(eintrag?.anzahl ?? 0)}`
    + `${platz.country ? ` · ${platz.country}` : ''}</span>`
    + (eintrag?.verortet ? ` ${marke(platz) || '<span class="herkunftsmarke">verortet</span>'}` : '');

  const anker = waehleAnker(platz, zustand.plaetze, zustand.tauchgaenge);
  zustand.karte.setView([anker.lat, anker.lon], anker.zoom);

  zeichneVorschlaege(wurzel);

  // Das Umfeld zur Orientierung — ohne die Plätze, die schon als Vorschlag
  // dastehen. Zweimal derselbe Punkt in zwei Farben liest sich als zwei Punkte.
  const schonGezeichnet = new Set(zustand.vorschlaege.map((v) => v.id));
  zustand.ebeneUmfeld.clearLayers();
  const umfeld = zustand.plaetze.filter((p) => p.id !== id && !schonGezeichnet.has(p.id));
  for (const nachbar of nachbarn(anker, umfeld)) {
    L.circleMarker([nachbar.lat, nachbar.lon], {
      radius: 5, color: '#7fa8c4', weight: 1, fillOpacity: 0.35,
    }).bindTooltip(nachbar.name ?? '', { direction: 'top' }).addTo(zustand.ebeneUmfeld);
  }

  // Der Ausschnitt richtet sich nach den Vorschlägen, nicht nach dem Anker:
  // Ein Vorschlag, den man erst suchen muss, ist keiner. Der Platz selbst kommt
  // dazu, falls er schon eine Position hat — sonst sieht man beim Korrigieren
  // nicht, was man korrigiert.
  const punkte = zustand.vorschlaege.map((v) => [v.lat, v.lon]);
  if (eintrag?.verortet) punkte.push([platz.lat, platz.lon]);
  if (punkte.length > 0) {
    zustand.karte.fitBounds(L.latLngBounds(punkte), { padding: [60, 60], maxZoom: 13 });
    const anzahl = zustand.vorschlaege.length;
    wurzel.querySelector('.ankergrund').textContent =
      `Ausschnitt über ${anzahl} ${anzahl === 1 ? 'Vorschlag' : 'Vorschläge'}`
      + (eintrag?.verortet ? ' und die bisherige Position' : '');
  } else {
    wurzel.querySelector('.ankergrund').textContent = GRUND_TEXT[anker.grund] ?? '';
  }

  if (eintrag?.verortet) setzeNadel(wurzel, platz.lat, platz.lon, 'bisherige Position');
  zeichneListe(wurzel);
  zeichneReiseplan(wurzel);
}

/**
 * Nach dem Speichern zum nächsten offenen Platz — das ist der Fliessbandteil.
 *
 * Im Reisemodus folgt er der Reise und nicht der Gesamtliste: Wer eine Reise
 * abarbeitet, will als Nächstes den Tauchgang danach und nicht den Platz mit
 * der zweithöchsten Tauchgangszahl irgendwo auf der Welt.
 */
function naechsterOffener(wurzel, nachId) {
  const { zustand } = wurzel;
  if (zustand.modus === 'reise' && zustand.reise) {
    const zeilen = reiseplan(zustand.reise, zustand.plaetze, zustand.tauchgaenge)
      .flatMap((tag) => tag.eintraege)
      .filter((z) => !z.verortet || z.platz.id === nachId);
    const stelle = zeilen.findIndex((z) => z.platz.id === nachId);
    const weiter = zeilen.slice(stelle + 1).find((z) => !z.verortet)
      ?? zeilen.find((z) => !z.verortet && z.platz.id !== nachId);
    return weiter?.platz.id ?? null;
  }
  const sichtbar = filtere(zustand.eintraege.filter((e) => !e.verortet), zustand.suchtext);
  if (sichtbar.length === 0) return null;
  const stelle = sichtbar.findIndex((e) => e.platz.id === nachId);
  return sichtbar[stelle + 1]?.platz.id ?? sichtbar[0].platz.id;
}

// --------------------------------------------------------------- Schreibweg

async function speichereNadel(wurzel) {
  const { zustand, db } = wurzel;
  const platz = zustand.plaetze.find((p) => p.id === zustand.gewaehlt);
  if (!platz || !zustand.nadelPunkt) return;

  const vorher = { ...platz };
  // Vor dem Schreiben ablesen: Nachher steht die eigene Nadel schon drin, und
  // "war die Reise ohne Anker?" liesse sich nicht mehr beantworten.
  const warOhneAnker = zustand.modus === 'reise' && zustand.reise
    && zustand.reisen.find((r) => r.id === zustand.reise)?.verortet === 0;
  try {
    const neu = await setzePosition(db, platz, zustand.nadelPunkt.lat, zustand.nadelPunkt.lon,
      { geraet: geraeteKennung() });
    aktualisiere(wurzel, neu);
    zustand.zuletzt = vorher;
    wurzel.querySelector('.zurueck').disabled = false;
    wurzel.aufAenderung();

    const weiter = naechsterOffener(wurzel, neu.id);
    // Die erste Nadel einer Reise ist die folgenreichste: Sie verwandelt jeden
    // übrigen Platz von "kein Anhaltspunkt" in einen mit Nachbarn.
    const offenDanach = zustand.reisen.find((r) => r.id === zustand.reise)?.offen ?? 0;
    const kaskade = warOhneAnker && offenDanach > 0
      ? ` · erste Position dieser Reise — ${weiterePlaetze(offenDanach)} jetzt einen Anhaltspunkt`
      : '';
    meldung(wurzel, `${neu.name ?? 'Platz'} gesetzt.${kaskade}`);
    if (weiter && weiter !== neu.id) {
      waehle(wurzel, weiter);
      const naechster = zustand.plaetze.find((p) => p.id === weiter);
      meldung(wurzel, `${neu.name ?? 'Platz'} gesetzt${kaskade} · weiter mit `
        + `${naechster?.name ?? 'dem nächsten Platz'}.`);
    } else {
      zeichneKopf(wurzel);
      zeichneListe(wurzel);
    }
  } catch (fehler) {
    // Nicht weiterrücken — sonst verlöre man den Platz aus dem Blick.
    meldung(wurzel, `Nicht gespeichert: ${fehler.message}`, true);
  }
}

/**
 * Den letzten Schreibvorgang zurücknehmen — mitsamt der Herkunftsmarke.
 *
 * Zurückgeschrieben wird der ganze vorherige Satz, nicht nur `lat`/`lon`: Sonst
 * bliebe `geocode_source: 'manual'` an einem Platz stehen, der wieder ohne
 * Position dasteht.
 */
async function nimmZurueck(wurzel) {
  const { zustand, db } = wurzel;
  if (!zustand.zuletzt) return;
  try {
    await speichere(db, SPEICHER.sites, zustand.zuletzt, { geraet: geraeteKennung() });
    aktualisiere(wurzel, zustand.zuletzt);
    const zurueck = zustand.zuletzt;
    zustand.zuletzt = null;
    wurzel.querySelector('.zurueck').disabled = true;
    wurzel.aufAenderung();
    waehle(wurzel, zurueck.id);
    meldung(wurzel, `${zurueck.name ?? 'Platz'} zurückgenommen — bitte neu setzen.`);
  } catch (fehler) {
    meldung(wurzel, `Nicht zurückgenommen: ${fehler.message}`, true);
  }
}

/**
 * Zwei Plätze zu einem verschmelzen.
 *
 * Der Fall, für den der Knopf da ist: Ein Alt-Platz und ein verorteter tragen
 * denselben Namen in zwei Schreibweisen. Ihn zu verorten wäre falsch — es gäbe
 * ihn danach zweimal, einmal mit und einmal ohne Tauchgänge daran.
 *
 * Anders als beim Setzen einer Position gibt es hier **kein Zurück**: Ein
 * Stammsatz verschwindet endgültig und hinterlässt einen Grabstein. Deshalb
 * die Rückfrage mit beiden Namen, beiden Tauchgangszahlen und dem
 * ausgeschriebenen Ergebnis, bevor irgendetwas geschrieben wird.
 */
async function verschmelze(wurzel, vorschlag) {
  const { zustand, db } = wurzel;
  const offen = zustand.plaetze.find((p) => p.id === zustand.gewaehlt);
  const treffer = zustand.plaetze.find((p) => p.id === vorschlag?.id);
  if (!offen || !treffer) return;

  const zahl = (id) => zustand.eintraege.find((e) => e.platz.id === id)?.anzahl ?? 0;
  const bleibt = await frageZusammenfuehren(
    { satz: offen, anzahl: zahl(offen.id) },
    { satz: treffer, anzahl: zahl(treffer.id) },
  );
  if (!bleibt) return;

  const weicht = bleibt.satz.id === offen.id ? treffer : offen;
  try {
    const { umgehaengt } = await fuehreZusammen(db, 'sites', bleibt.satz, weicht,
      { geraet: geraeteKennung() });
    // Umgehängte Tauchgänge und ein gelöschter Platz — die Ablagen der Ansicht
    // sind danach in beiden Richtungen veraltet. Frisch lesen ist hier billiger
    // als jeden Fall von Hand nachzuziehen.
    await ladeNeu(wurzel);
    zustand.zuletzt = null;
    wurzel.querySelector('.zurueck').disabled = true;
    wurzel.aufAenderung();

    const weiter = zustand.eintraege.find((e) => !e.verortet && e.platz.id !== weicht.id);
    if (weiter) waehle(wurzel, weiter.platz.id);
    else {
      zustand.gewaehlt = null;
      zeichneListe(wurzel);
    }
    meldung(wurzel, `„${weicht.name ?? 'Platz'}" ging in „${bleibt.satz.name}" auf`
      + (umgehaengt ? `, ${tauchgangszahl(umgehaengt)} umgehängt.` : '.'));
  } catch (fehler) {
    meldung(wurzel, `Nicht zusammengeführt: ${fehler.message}`, true);
  }
}

/** Plätze und Tauchgänge frisch aus der Datenbank holen. */
async function ladeNeu(wurzel) {
  const { zustand, db } = wurzel;
  zustand.plaetze = await alleAus(db, SPEICHER.sites);
  zustand.tauchgaenge = await alleTauchgaenge(db);
  zustand.eintraege = arbeitsliste(zustand.plaetze, zustand.tauchgaenge);
  zustand.reisen = reiseListe(zustand.plaetze, zustand.tauchgaenge);
  zeichneKopf(wurzel);
  zeichneReisewahl(wurzel);
}

/** Den geänderten Platz in allen Ablagen der Ansicht nachziehen. */
function aktualisiere(wurzel, neu) {
  const { zustand } = wurzel;
  const i = zustand.plaetze.findIndex((p) => p.id === neu.id);
  if (i >= 0) zustand.plaetze[i] = neu;
  zustand.eintraege = arbeitsliste(zustand.plaetze, zustand.tauchgaenge);
  // Die Zählung der Reise ändert sich mit jeder gesetzten Nadel — der
  // Fortschritt ist das, was man beim Abarbeiten sehen will.
  zustand.reisen = reiseListe(zustand.plaetze, zustand.tauchgaenge);
  zeichneKopf(wurzel);
  zeichneReisewahl(wurzel);
}

// ------------------------------------------------------------------- Gerüst

function starteKarte(wurzel) {
  const behaelter = wurzel.querySelector('#verortungskarte');
  // Warten, bis die Ansicht im Dokument hängt — aber über einen Bildlauf und
  // nicht über eine Mikroaufgabe: Eine Mikroaufgabe, die sich selbst neu
  // einreiht, lässt die Warteschlange nie leer werden. Seit die Ansichten mit
  // `startViewTransition` gewechselt werden, hängt das Anhängen an einer
  // gewöhnlichen Aufgabe — und die kam hinter dieser Schleife nie an die
  // Reihe. Der Reiter blieb stehen.
  if (!behaelter.isConnected) { requestAnimationFrame(() => starteKarte(wurzel)); return; }

  const { zustand } = wurzel;
  zustand.karte = L.map(behaelter,
    { worldCopyJump: true, attributionControl: false });
  L.control.attribution({ prefix: false }).addTo(zustand.karte);
  zustand.loeseGroesse = haltGroesseNach(zustand.karte, behaelter);
  zustand.grund.anKarte(zustand.karte);
  zustand.ebeneUmfeld = L.layerGroup().addTo(zustand.karte);
  zustand.ebeneVorschlaege = L.layerGroup().addTo(zustand.karte);
  zustand.karte.setView([20, 10], 2);

  zustand.karte.on('click', (ereignis) => {
    setzeNadel(wurzel, ereignis.latlng.lat, ereignis.latlng.lng);
  });

  const erster = zustand.eintraege.find((e) => !e.verortet);
  if (erster) waehle(wurzel, erster.platz.id);
  else zeichneListe(wurzel);
}

export async function ansichtVerorten(db, aufAenderung) {
  const wurzel = document.createElement('section');
  wurzel.className = 'verorten';
  wurzel.innerHTML = `
    <header class="verorten-kopf">
      <h1>Koordinaten nachtragen</h1>
      <p class="fortschritt"></p>
    </header>
    <button type="button" class="knopf listen-schalter" aria-expanded="false">
      Plätze wählen</button>
    <div class="verorten-inhalt">
      <div class="verorten-liste">
        <div class="verortmodus" role="group" aria-label="Arbeitsweise">
          <button type="button" class="knopf modus aktiv" data-modus="alle"
            aria-pressed="true">Alle Plätze</button>
          <button type="button" class="knopf modus" data-modus="reise"
            aria-pressed="false">Nach Reise</button>
        </div>
        <select class="reisewahl" aria-label="Reise wählen" hidden></select>
        <input type="search" class="platzsuche" placeholder="Platz, Land, Reise …"
          aria-label="Plätze durchsuchen">
        <label class="auch-verortete">
          <input type="checkbox"> auch verortete zeigen
        </label>
        <p class="listenstand"></p>
        <ol class="schlange"></ol>
        <div class="reiseplan" hidden></div>
        <button type="button" class="mehr-laden" hidden></button>
      </div>
      <div class="verorten-karte">
        <div class="verorten-kartenkopf">
          <p class="gewaehlter-platz">Kein Platz gewählt.</p>
          ${grundwahlLeiste()}
        </div>
        <div id="verortungskarte"></div>
        <p class="ankergrund"></p>
        <div class="nadelzeile">
          <input type="text" class="koordinatenfeld"
            placeholder="46.9757, 8.3403 · 46°58'32&quot;N 8°20'25&quot;E · Google-Maps-Link"
            aria-label="Koordinaten einfügen">
          <button type="button" class="knopf springen">Nadel setzen</button>
        </div>
        <p class="nadelstand"></p>
        <div class="verorten-knoepfe">
          <button type="button" class="knopf speichern" disabled>Position speichern</button>
          <button type="button" class="knopf nadel-weg" disabled>Nadel entfernen</button>
          <button type="button" class="knopf zurueck" disabled>Rückgängig</button>
          <span class="meldung" role="status" aria-live="polite"></span>
        </div>
        <div class="vorschlaege"></div>
      </div>
    </div>`;

  const plaetze = await alleAus(db, SPEICHER.sites);
  const tauchgaenge = await alleTauchgaenge(db);
  wurzel.zustand = {
    plaetze,
    tauchgaenge,
    eintraege: arbeitsliste(plaetze, tauchgaenge),
    reisen: reiseListe(plaetze, tauchgaenge),
    alleReisen: reiseListe(plaetze, tauchgaenge, { nurOffene: false }),
    modus: 'alle',
    reise: null,
    gewaehlt: null,
    suchtext: '',
    auchVerortete: false,
    grenze: 200,          // erst einmal genug; "mehr zeigen" hängt den Rest an
    vorschlaege: [],
    zuletzt: null,
    karte: null,
    ebeneUmfeld: null,
    ebeneVorschlaege: null,
    nadel: null,
    nadelPunkt: null,
    grund: grundwahl(wurzel.querySelector('.grundwahl')),
  };
  wurzel.db = db;
  wurzel.aufAenderung = aufAenderung;

  zeichneKopf(wurzel);
  zeichneNadelstand(wurzel);
  zeichneReisewahl(wurzel);

  wurzel.querySelector('.platzsuche').addEventListener('input', (ereignis) => {
    wurzel.zustand.suchtext = ereignis.target.value;
    wurzel.zustand.grenze = 200;
    zeichneListe(wurzel);
  });
  wurzel.querySelector('.auch-verortete input').addEventListener('change', (ereignis) => {
    wurzel.zustand.auchVerortete = ereignis.target.checked;
    zeichneListe(wurzel);
  });
  wurzel.querySelector('.mehr-laden').addEventListener('click', () => {
    wurzel.zustand.grenze += 200;
    zeichneListe(wurzel);
  });
  wurzel.querySelector('.schlange').addEventListener('click', (ereignis) => {
    const knopf = ereignis.target.closest('button[data-id]');
    if (knopf) waehle(wurzel, knopf.dataset.id);
  });
  wurzel.querySelector('.reiseplan').addEventListener('click', (ereignis) => {
    const knopf = ereignis.target.closest('button[data-id]');
    if (knopf) waehle(wurzel, knopf.dataset.id);
  });
  wurzel.querySelector('.verortmodus').addEventListener('click', (ereignis) => {
    const knopf = ereignis.target.closest('button[data-modus]');
    if (!knopf || knopf.dataset.modus === wurzel.zustand.modus) return;
    wurzel.zustand.modus = knopf.dataset.modus;
    zeichneModus(wurzel);
    if (wurzel.zustand.modus === 'reise') {
      const reise = wurzel.zustand.reise ?? wurzel.zustand.reisen[0]?.id;
      if (reise) waehleReise(wurzel, reise);
    }
  });
  wurzel.querySelector('.reisewahl').addEventListener('change', (ereignis) => {
    waehleReise(wurzel, ereignis.target.value);
  });
  wurzel.querySelector('.vorschlaege').addEventListener('click', (ereignis) => {
    const verschmelzen = ereignis.target.closest('button[data-verschmelzen]');
    if (verschmelzen) {
      verschmelze(wurzel, wurzel.zustand.vorschlaege[Number(verschmelzen.dataset.verschmelzen)]);
      return;
    }
    const knopf = ereignis.target.closest('button[data-vorschlag]');
    if (!knopf) return;
    const eintrag = wurzel.zustand.vorschlaege[Number(knopf.dataset.vorschlag)];
    if (eintrag) setzeNadel(wurzel, eintrag.lat, eintrag.lon, `Vorschlag „${eintrag.name}"`);
  });

  const feld = wurzel.querySelector('.koordinatenfeld');
  const ausFeld = () => {
    const punkt = leseKoordinaten(feld.value);
    if (!punkt) {
      meldung(wurzel, 'Daraus lässt sich keine Position lesen. Erwartet werden zwei '
        + 'Zahlen, Grad/Minuten/Sekunden oder ein Google-Maps-Link.', true);
      return;
    }
    meldung(wurzel, '');
    wurzel.zustand.karte?.setView([punkt.lat, punkt.lon], ZOOM_NADEL);
    setzeNadel(wurzel, punkt.lat, punkt.lon, 'eingefügt');
  };
  wurzel.querySelector('.springen').addEventListener('click', ausFeld);
  feld.addEventListener('keydown', (ereignis) => {
    if (ereignis.key === 'Enter') { ereignis.preventDefault(); ausFeld(); }
  });

  wurzel.querySelector('.speichern').addEventListener('click', () => speichereNadel(wurzel));
  wurzel.querySelector('.nadel-weg').addEventListener('click', () => entferneNadel(wurzel));
  wurzel.querySelector('.zurueck').addEventListener('click', () => nimmZurueck(wurzel));

  /**
   * Auf dem Telefon ist die Karte die Arbeitsfläche, nicht die Liste.
   *
   * Nebeneinander passen beide nur auf einen breiten Schirm; untereinander
   * schöbe die Liste die Karte aus dem Bild — und genau auf die Karte tippt
   * man hier. Die Liste klappt deshalb über einen Schalter auf und nach der
   * Wahl eines Platzes von selbst wieder zu. Auf breiten Schirmen ist der
   * Schalter unsichtbar und die Klasse wirkungslos.
   */
  const schalter = wurzel.querySelector('.listen-schalter');
  const klappe = (offen) => {
    wurzel.classList.toggle('listeoffen', offen);
    schalter.setAttribute('aria-expanded', String(offen));
    schalter.textContent = offen ? 'Liste schliessen' : 'Plätze wählen';
  };
  schalter.addEventListener('click', () => klappe(!wurzel.classList.contains('listeoffen')));
  wurzel.querySelector('.verorten-liste').addEventListener('click', (ereignis) => {
    if (!ereignis.target.closest('.schlangezeile button, .reisetag button')) return;
    klappe(false);
  });

  requestAnimationFrame(() => starteKarte(wurzel));

  wurzel.aufraeumen = () => {
    wurzel.zustand.loeseGroesse?.();
    wurzel.zustand.karte?.remove();
    wurzel.zustand.karte = null;
  };
  return wurzel;
}
